// frontend/src/components/looks/FollowButton.js
// W3 — Follow a professional (Staff). Account required to act; follower
// count is public. Guests clicking Follow are routed to login.
//
// Props:
//   staffId         — Staff._id (required)
//   initialCount    — number, optional seed for follower count
//   variant         — "pill" (default) | "icon"
//   apiBase         — optional API base override
//   onChanged       — optional fn({ following, followerCount }) callback

import React, { useState, useCallback, useEffect } from "react";
import { FaUserPlus, FaCheck } from "react-icons/fa";
import AuthRequiredModal from "../AuthRequiredModal";

const DEFAULT_API = process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

function FollowButton({
  staffId,
  initialCount = 0,
  variant = "pill",
  apiBase = DEFAULT_API,
  onChanged,
}) {
  const [following, setFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(Number(initialCount) || 0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showAuth, setShowAuth] = useState(false);

  const getToken = () => localStorage.getItem("ogestyle_token");

  // Load current follow state + follower count when authenticated.
  const loadState = useCallback(async () => {
    if (!staffId) return;
    try {
      const headers = {};
      const token = getToken();
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(`${apiBase}/api/looks-social/staff/${staffId}/engagement`, {
        headers,
      });
      const data = await res.json();
      if (!res.ok) return;
      setFollowerCount(Number(data.followerCount) || 0);
      if (token) setFollowing(!!data.isFollowing);
    } catch (err) {
      // Non-fatal — keep defaults.
    }
  }, [staffId, apiBase]);

  useEffect(() => {
    loadState();
  }, [loadState]);

  const toggle = async () => {
    setError("");
    const token = getToken();
    if (!token) {
      setShowAuth(true);
      return;
    }
    setBusy(true);
    try {
      const method = following ? "DELETE" : "POST";
      const res = await fetch(`${apiBase}/api/looks-social/follow/${staffId}`, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.message || "Unable to update follow");
        return;
      }
      const next = !!data.following;
      setFollowing(next);
      setFollowerCount(Number(data.followerCount) || 0);
      if (typeof onChanged === "function") {
        onChanged({ following: next, followerCount: Number(data.followerCount) || 0 });
      }
    } catch (err) {
      setError("Unable to update follow");
    } finally {
      setBusy(false);
    }
  };

  const modal = (
    <AuthRequiredModal
      open={showAuth}
      onClose={() => setShowAuth(false)}
      intent="follow this professional"
      onAuthed={() => {
        setShowAuth(false);
        toggle();
      }}
    />
  );

  const pillStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    border: following ? "1px solid #1d4f80" : "1px solid #1d4f80",
    background: following ? "#1d4f80" : "#ffffff",
    color: following ? "#fff" : "#1d4f80",
    borderRadius: "999px",
    padding: "8px 16px",
    fontSize: "13px",
    fontWeight: 700,
    cursor: busy ? "default" : "pointer",
    opacity: busy ? 0.6 : 1,
    transition: "background 0.15s ease, color 0.15s ease",
    boxShadow: "0 2px 6px rgba(29,79,128,0.18)",
  };

  if (variant === "icon") {
    return (
      <>
        <button
          onClick={toggle}
          disabled={busy}
          title={following ? "Following" : "Follow"}
          aria-label={following ? "Following" : "Follow"}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "5px",
            border: "none",
            background: "transparent",
            color: following ? "#1d4f80" : "#64748b",
            fontSize: "12px",
            fontWeight: 600,
            cursor: busy ? "default" : "pointer",
            padding: "0",
          }}
        >
          {following ? <FaCheck size={13} /> : <FaUserPlus size={13} />}
          <span>{following ? "Following" : "Follow"}</span>
          {followerCount > 0 && <span style={{ color: "#9ca3af" }}>{followerCount.toLocaleString()}</span>}
        </button>
        {modal}
      </>
    );
  }

  return (
    <>
      <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
        <button onClick={toggle} disabled={busy} style={pillStyle}>
          {following ? <FaCheck size={13} /> : <FaUserPlus size={13} />}
          {following ? "Following" : "Follow"}
        </button>
        {followerCount > 0 && (
          <span style={{ fontSize: "11px", color: "#6b7280" }}>
            {followerCount.toLocaleString()} follower{followerCount === 1 ? "" : "s"}
          </span>
        )}
        {error && <span style={{ fontSize: "11px", color: "#b91c1c" }}>{error}</span>}
      </div>
      {modal}
    </>
  );
}

export default FollowButton;
